import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrum from '../../components/breadcrum'
import { toast } from 'react-hot-toast'
import {
  createTrending,
  updateTrending,
  getTrendingById,
} from '../../api/trendingApi'
import { createSample, getSampleById, updateSample } from '../../api/sampleApi'

const AddSample = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)
  const [pdfPreview, setPdfPreview] = useState(null)

  useEffect(() => {
    document.title = `${isEditMode ? 'Edit ' : 'Add '}Sample Report`
  }, [isEditMode])

  // Initialize form with default values
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      pdf: null,
    },
  })

  // Watch the pdf field to access it in the submission
  const pdfField = watch('pdf')

  // Fetch sample data if in edit mode
  const { data: sampleData, isLoading: isLoadingSample } = useQuery({
    queryKey: ['sample', id],
    queryFn: () => getSampleById(id),
    enabled: isEditMode,
    onSuccess: (data) => {
      console.log('Query success handler triggered with data:', data)
      populateForm(data)
    },
    onError: (error) => {
      toast.error('Failed to load sample data')
      console.error('Error loading sample:', error)
      navigate('/dealdashboard/sample') // Redirect back to sample on error
    },
  })

  // Separate function to handle form population to ensure it runs
  const populateForm = (data) => {
    if (!data) {
      console.error('No data available to populate form')
      return
    }

    console.log('Populating form with data:', data)

    // Use timeout to ensure this runs after render cycle
    setTimeout(() => {
      setValue('title', data.title || '')
    }, 0)
  }

  // Ensure form is populated when data changes
  useEffect(() => {
    if (isEditMode && sampleData) {
      console.log('sampleData updated in useEffect:', sampleData)
      populateForm(sampleData)
    }
  }, [sampleData, isEditMode])

  // Create pdf preview when pdf field changes
  useEffect(() => {
    if (pdfField && pdfField[0]) {
      const file = pdfField[0]
      const previewUrl = URL.createObjectURL(file)
      setPdfPreview(previewUrl)

      // Clean up the URL when component unmounts or pdf changes
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [pdfField])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createSample,
    onSuccess: () => {
      toast.success('Sample added successfully!')
      reset()
      setPdfPreview(null)
      navigate('/dealdashboard/sample') // Redirect to Sample
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to create Sample')
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateSample(id, data),
    onSuccess: () => {
      toast.success('Sample updated successfully!')
      navigate('/dealdashboard/sample') // Redirect to Sample
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to update Sample')
    },
  })

  const handlePdfChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      // Set the file to the form
      setValue('pdf', files)
    }
  }

  const onSubmit = (data) => {
    console.log('Form submitted with data:', data)

    // In edit mode, pdf is optional
    if (!isEditMode && (!data.pdf || !data.pdf[0])) {
      toast.error('Please upload pdf')
      return
    }

    // Submit the form data based on mode
    if (isEditMode) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  // Show loading state while fetching data in edit mode
  if (isEditMode && isLoadingSample) {
    return (
      <>
        <Breadcrum heading="sample" subheading="Edit Sample" />
        <div className="max-w-8xl text-center mx-auto px-4 py-6">
          <p className="text-gray-600">Loading Sample data...</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum
        heading="sample"
        subheading={isEditMode ? 'Edit Sample' : 'Add Sample'}
      />
      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        {isEditMode && sampleData && (
          <div className="bg-blue-50 p-4 mb-6 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-800">Editing Sample:</h3>
            <p className="text-blue-700">{sampleData.title}</p>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Heading Input */}
          <div className="bg-white p-6 rounded-xl shadow mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Title</h2>
            <input
              type="text"
              placeholder="Enter headline..."
              className={`border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
              defaultValue={sampleData?.title || ''}
              {...register('title', {
                required: 'Headline is required',
              })}
            />
            {errors.title && (
              <p className="mt-1 text-red-500 text-sm">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Image Upload */}
          <div className="bg-white p-6 rounded-xl shadow mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Pdf</h2>
            {pdfPreview && (
              <object
                data={pdfPreview}
                type="application/pdf"
                className="w-full h-96 mt-4 rounded border border-gray-200"
              >
                <p>
                  Your browser does not support PDFs.{' '}
                  <a href={pdfPreview}>Download PDF</a>.
                </p>
              </object>
            )}
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfChange}
              className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
              {...register('pdf', {
                required: isEditMode ? false : 'pdf is required',
              })}
            />
            {errors.pdf && (
              <p className="mt-1 text-red-500 text-sm">{errors.pdf.message}</p>
            )}
            {isEditMode && (
              <p className="mt-1 text-gray-500 text-sm">
                Upload a new pdf only if you want to replace the current one.
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all disabled:bg-orange-300 disabled:cursor-not-allowed"
            >
              {createMutation.isPending || updateMutation.isPending
                ? isEditMode
                  ? 'Updating...'
                  : 'Adding...'
                : isEditMode
                ? 'Update'
                : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dealdashboard/sample')}
              className="ml-4 bg-gray-100 hover:bg-gray-200 text-gray-800 text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddSample
